import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { api } from '../services/api';
import BillingBreakdownDisplay, { BillingBreakdownLine } from './BillingBreakdownDisplay';
import { calculateBillingPreview } from '../utils/clientBillingCalculation';

interface ClientEstimate {
  id: number;
  case_id: number;
  client_firm_id: number;
  billing_type: string;
  rate: number;
  quantity: number;
  total_pages?: number | null;
  reviewed_pages?: number | null;
  appendix_c_rate?: number | null;
  appendix_c_quantity?: number;
  appendix_included?: boolean;
  billing_breakdown?: { lines: BillingBreakdownLine[] } | null;
  total_amount: number;
  additional_claimant?: boolean;
  description: string;
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  created_by: number | null;
  approved_by: number | null;
  approved_at: string | null;
  rejection_reason: string | null;
  is_invoiced: boolean;
  created_at: string;
  clientFirm: {
    id: number;
    name: string;
  };
  createdBy?: {
    id: number;
    name: string;
  };
  approvedBy?: {
    id: number;
    name: string;
  };
}

interface User {
  id: number;
  name: string;
  email: string;
  roles?: Array<{ slug: string; name: string }>;
}

interface ClientEstimationProps {
  caseId: number;
  projectId: number;
  user: User | null;
}

const ClientEstimation: React.FC<ClientEstimationProps> = ({
  caseId,
  projectId,
  user,
}) => {
  const [estimates, setEstimates] = useState<ClientEstimate[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingEstimate, setEditingEstimate] = useState<ClientEstimate | null>(null);
  const [projectSettings, setProjectSettings] = useState<any>(null);

  const [formData, setFormData] = useState({
    quantity: '',
    total_pages: '',
    reviewed_pages: '',
    appendix_included: false,
    description: '',
    additional_claimant: false,
  });

  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectingEstimate, setRejectingEstimate] = useState<ClientEstimate | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const billingType = projectSettings?.custom_fields?.billing_type as string | undefined;
  const isPageBased = billingType === 'Page Based';
  const isConsolidation = billingType === 'Consolidation';
  const isPageBilling = isPageBased || isConsolidation;
  const hasAppendixConfig = isPageBased
    ? (projectSettings?.custom_fields?.appendix_c_rate_ranges?.length > 0 || Number(projectSettings?.custom_fields?.appendix_c_rate) > 0)
    : isConsolidation && Number(projectSettings?.custom_fields?.appendix_c_rate) > 0;

  const isPmpAdmin = () => user?.roles?.some((role) => role.slug === 'pmp_admin') ?? false;
  const isSiteAdmin = () => user?.roles?.some((role) => role.slug === 'site_admin') ?? false;
  const isPmpAdminOrSiteAdmin = () => isPmpAdmin() || isSiteAdmin();

  const isClientUser = () => {
    if (!user?.roles) return false;
    return user.roles.some((role) =>
      ['firm_admin', 'firm_main', 'client_admin', 'client_manager'].includes(role.slug)
    );
  };

  const canViewBreakdown = isPmpAdmin() || isClientUser();

  const preview = useMemo(
    () => calculateBillingPreview(billingType, projectSettings?.custom_fields, formData, projectSettings?.default_page_rate),
    [billingType, projectSettings?.custom_fields, projectSettings?.default_page_rate, formData]
  );

  const isClientFirmUser = (estimate: ClientEstimate) => {
    if (!user?.roles) return false;
    return user.roles.some((role) => ['firm_admin', 'firm_main'].includes(role.slug));
  };

  useEffect(() => {
    loadEstimates();
    loadProjectSettings();
  }, [caseId, projectId]);

  const loadEstimates = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/cases/${caseId}/client-estimates`);
      if (response.data.success) {
        setEstimates(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load client estimates');
    } finally {
      setLoading(false);
    }
  };

  const loadProjectSettings = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/settings`);
      if (response.data.success) {
        setProjectSettings(response.data.data);
      }
    } catch (err: any) {
      console.error('Failed to load project settings:', err);
    }
  };

  const buildPayload = () => {
    if (isPageBased) {
      return {
        total_pages: parseInt(formData.total_pages, 10),
        reviewed_pages: parseInt(formData.reviewed_pages, 10),
        appendix_included: formData.appendix_included,
        description: formData.description,
      };
    }
    if (isConsolidation) {
      return {
        total_pages: parseInt(formData.total_pages, 10),
        appendix_included: formData.appendix_included,
        description: formData.description,
      };
    }
    const payload: Record<string, unknown> = {
      quantity: parseInt(formData.quantity, 10),
      description: formData.description,
    };
    if (projectSettings?.custom_fields?.billing_type === 'Flat Rate' && (projectSettings?.custom_fields?.additional_claimant ?? 0) > 0) {
      payload.additional_claimant = formData.additional_claimant;
    }
    return payload;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isPageBased && (!formData.total_pages || !formData.reviewed_pages)) {
      setError('Please enter total pages and reviewed pages');
      return;
    }
    if (isConsolidation && !formData.total_pages) {
      setError('Please enter total pages');
      return;
    }
    if (!isPageBilling && !formData.quantity) {
      setError('Please enter quantity');
      return;
    }

    try {
      setSubmitting(true);
      const payload = buildPayload();

      const response = editingEstimate
        ? await api.put(`/client-estimates/${editingEstimate.id}/draft`, payload)
        : await api.post(`/cases/${caseId}/client-estimates`, payload);

      if (response.data.success) {
        setSuccess(editingEstimate ? 'Estimate updated successfully' : 'Estimate created successfully');
        setFormData({
          quantity: '',
          total_pages: '',
          reviewed_pages: '',
          appendix_included: false,
          description: '',
          additional_claimant: false,
        });
        setEditingEstimate(null);
        loadEstimates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save estimate');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditDraft = (estimate: ClientEstimate) => {
    setEditingEstimate(estimate);
    setFormData({
      quantity: estimate.quantity?.toString() || '',
      total_pages: estimate.total_pages?.toString() || '',
      reviewed_pages: estimate.reviewed_pages?.toString() || '',
      appendix_included: Boolean(estimate.appendix_included),
      description: estimate.description || '',
      additional_claimant: Boolean(estimate.additional_claimant),
    });
  };

  const handleCancelEdit = () => {
    setEditingEstimate(null);
    setFormData({
      quantity: '',
      total_pages: '',
      reviewed_pages: '',
      appendix_included: false,
      description: '',
      additional_claimant: false,
    });
  };

  const handleSubmitForApproval = async (estimate: ClientEstimate) => {
    try {
      const response = await api.post(`/client-estimates/${estimate.id}/submit`);
      if (response.data.success) {
        setSuccess('Estimate submitted for client approval');
        loadEstimates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit estimate');
    }
  };

  const handleApprove = async (estimate: ClientEstimate) => {
    try {
      const response = await api.put(`/client-estimates/${estimate.id}/status`, { action: 'approve' });
      if (response.data.success) {
        setSuccess('Estimate approved successfully');
        loadEstimates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to approve estimate');
    }
  };

  const handleRejectClick = (estimate: ClientEstimate) => {
    setRejectingEstimate(estimate);
    setRejectionReason('');
    setShowRejectDialog(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectingEstimate || !rejectionReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }

    try {
      const response = await api.put(`/client-estimates/${rejectingEstimate.id}/status`, {
        action: 'reject',
        rejection_reason: rejectionReason,
      });
      if (response.data.success) {
        setSuccess('Estimate rejected successfully');
        setShowRejectDialog(false);
        setRejectingEstimate(null);
        setRejectionReason('');
        loadEstimates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reject estimate');
    }
  };

  const getBillingTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      page_based: 'Page Based',
      time_based: 'Time Based',
      case_based: 'Case Based',
      flat_rate: 'Flat Rate',
      consolidation: 'Consolidation',
    };
    return labels[type] || type;
  };

  const getQuantityLabel = (type: string) => {
    const labels: Record<string, string> = {
      page_based: 'pages',
      consolidation: 'pages',
      time_based: 'hours',
      case_based: 'cases',
      flat_rate: 'pages',
    };
    return labels[type] || 'units';
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'default',
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Estimate History</Typography>
        <Divider sx={{ mb: 2 }} />

        {estimates.length === 0 ? (
          <Typography color="text.secondary">No estimates found for this case.</Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Client Firm</TableCell>
                  <TableCell>Billing Type</TableCell>
                  <TableCell>Pages</TableCell>
                  {canViewBreakdown && <TableCell>Total Amount</TableCell>}
                  <TableCell>Status</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {estimates.map((estimate) => (
                  <TableRow key={estimate.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {estimate.clientFirm?.name || 'Unknown Firm'}
                      </Typography>
                    </TableCell>
                    <TableCell>{getBillingTypeLabel(estimate.billing_type)}</TableCell>
                    <TableCell>
                      {estimate.total_pages != null ? (
                        <>
                          Total: {estimate.total_pages}
                          {estimate.reviewed_pages != null && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              Reviewed: {estimate.reviewed_pages}
                            </Typography>
                          )}
                          {estimate.appendix_included && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              Appendix included
                            </Typography>
                          )}
                        </>
                      ) : (
                        `${estimate.quantity} ${getQuantityLabel(estimate.billing_type)}`
                      )}
                    </TableCell>
                    {canViewBreakdown && (
                      <TableCell>${Number(estimate.total_amount).toFixed(2)}</TableCell>
                    )}
                    <TableCell>
                      <Chip label={estimate.status.toUpperCase()} color={getStatusColor(estimate.status) as any} size="small" />
                      {estimate.is_invoiced && <Chip label="INVOICED" color="info" size="small" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell>{new Date(estimate.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {isPmpAdmin() && estimate.status === 'draft' && (
                          <>
                            <Button size="small" variant="outlined" onClick={() => handleEditDraft(estimate)}>Edit</Button>
                            <Button size="small" variant="contained" color="primary" onClick={() => handleSubmitForApproval(estimate)}>
                              Submit for Approval
                            </Button>
                          </>
                        )}
                        {estimate.status === 'pending' && (isPmpAdmin() || isClientFirmUser(estimate)) && (
                          <>
                            <Button size="small" variant="contained" color="success" onClick={() => handleApprove(estimate)}>Approve</Button>
                            <Button size="small" variant="contained" color="error" onClick={() => handleRejectClick(estimate)}>Reject</Button>
                          </>
                        )}
                      </Box>
                      {canViewBreakdown && estimate.billing_breakdown?.lines?.length ? (
                        <Box sx={{ mt: 1, maxWidth: 360 }}>
                          <BillingBreakdownDisplay
                            lines={estimate.billing_breakdown.lines}
                            totalAmount={Number(estimate.total_amount)}
                            title="Saved Breakdown"
                          />
                        </Box>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Client Billing Estimate</Typography>
        <Divider sx={{ mb: 2 }} />

        {billingType && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2"><strong>Billing Type:</strong> {billingType}</Typography>
            {isConsolidation && Number(projectSettings?.custom_fields?.appendix_c_rate) > 0 && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                <strong>Appendix Rate:</strong> ${projectSettings.custom_fields.appendix_c_rate} per page
              </Typography>
            )}
          </Alert>
        )}

        {isPmpAdminOrSiteAdmin() && (
          <form onSubmit={handleSubmit}>
            {isPageBased && (
              <>
                <TextField fullWidth label="Total Pages" type="number" value={formData.total_pages}
                  onChange={(e) => setFormData({ ...formData, total_pages: e.target.value })} margin="normal" required inputProps={{ min: 1 }} />
                <TextField fullWidth label="Reviewed Pages" type="number" value={formData.reviewed_pages}
                  onChange={(e) => setFormData({ ...formData, reviewed_pages: e.target.value })} margin="normal" required inputProps={{ min: 1 }} />
              </>
            )}

            {isConsolidation && (
              <TextField fullWidth label="Total Pages" type="number" value={formData.total_pages}
                onChange={(e) => setFormData({ ...formData, total_pages: e.target.value })} margin="normal" required inputProps={{ min: 1 }} />
            )}

            {isPageBilling && hasAppendixConfig && (
              <FormControlLabel
                control={<Checkbox checked={formData.appendix_included} onChange={(e) => setFormData({ ...formData, appendix_included: e.target.checked })} />}
                label="Appendix Included"
                sx={{ mt: 1, display: 'block' }}
              />
            )}

            {!isPageBilling && (
              <TextField fullWidth label="Quantity" type="number" value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} margin="normal" required
                helperText="Enter the quantity (pages, hours, or cases based on project settings)" />
            )}

            <TextField fullWidth label="Description" multiline rows={3} value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })} margin="normal"
              helperText="Optional description for this estimate" />

            {billingType === 'Flat Rate' && (projectSettings?.custom_fields?.additional_claimant ?? 0) > 0 && (
              <FormControlLabel
                control={<Checkbox checked={formData.additional_claimant} onChange={(e) => setFormData({ ...formData, additional_claimant: e.target.checked })} />}
                label={`Additional Claimant (+$${projectSettings.custom_fields.additional_claimant})`}
                sx={{ mt: 1 }}
              />
            )}

            {canViewBreakdown && isPageBilling && preview?.lines?.length ? (
              <Box sx={{ mt: 2, minHeight: 80 }}>
                <BillingBreakdownDisplay lines={preview.lines} totalAmount={preview.total_amount} />
              </Box>
            ) : null}

            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button type="submit" variant="contained" color="primary" disabled={submitting}>
                {submitting ? <CircularProgress size={24} /> : (editingEstimate ? 'Update Draft' : 'Save as Draft')}
              </Button>
              {editingEstimate && <Button variant="outlined" onClick={handleCancelEdit}>Cancel</Button>}
            </Box>
          </form>
        )}

        {!isPmpAdminOrSiteAdmin() && (
          <Alert severity="info">Client estimates are created by PMP administrators.</Alert>
        )}
      </Paper>

      <Dialog open={showRejectDialog} onClose={() => setShowRejectDialog(false)}>
        <DialogTitle>Reject Estimate</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Rejection Reason" multiline rows={4} value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)} margin="normal" required />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRejectDialog(false)}>Cancel</Button>
          <Button onClick={handleRejectConfirm} variant="contained" color="error">Reject Estimate</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientEstimation;
